/* Storefront guard. Pair with Shopify checkout validation for server enforcement. */
(() => {
  const config = JSON.parse(document.getElementById('mlf-purchase-config').textContent);
  const root = config.root.replace(/\/?$/, '/');
  const nativeFetch = window.fetch.bind(window);
  const limited = new Map();
  const bagChoices = new Map();
  const bagChoiceMessage = "Only one sac d’évasion can be added from this campaign. Please add a different product as your second item.";
  let bagChoiceRejected = false;
  const comingSoon = new Map();
  const comingSoonMessage = 'Coming soon. This item is not available to purchase yet. Please remove it to continue.';
  const variantProducts = new Map();
  const exclusive = new Map();
  const campaignMessage = 'A maximum of two items from this campaign’s limited selection may be purchased per order. Please remove any additional restricted items to proceed to checkout.';
  const products = new Map();
  let queue = Promise.resolve();
  let alertTimer;
  function scan() {
    document.querySelectorAll('[data-purchase-rule-product]').forEach(node => {
      const data = JSON.parse(node.textContent);
      data.variants.forEach(id => {
        comingSoon.set(String(id), data.comingSoon === true);
        limited.set(String(id), data.limited);
        bagChoices.set(String(id), data.chooseOneBag === true);
        exclusive.set(String(id), data.exclusive === true);
        if (data.productId != null) variantProducts.set(String(id), String(data.productId));
      });
    });
  }
  function notice(message) {
    let node = document.querySelector('.mlf-purchase-alert');
    if (!node) {
      node = document.createElement('div');
      node.className = 'mlf-purchase-alert';
      node.setAttribute('role', 'alert');
      document.body.append(node);
    }
    node.textContent = message;
    node.toggleAttribute('data-campaign-limit', message === campaignMessage);
    node.toggleAttribute('data-order-minimum', message.startsWith('Minimum order '));
    clearTimeout(alertTimer);
    alertTimer = setTimeout(() => node.remove(), 8000);
  }
  async function cart() {
    const response = await nativeFetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Your cart could not be checked. Please try again.');
    return response.json();
  }
  async function hydrate(items) {
    scan();
    await Promise.all(items.map(async item => {
      // Read current product tags for carts saved before the coming-soon tag was added.
      if (!products.has(item.handle)) products.set(item.handle, (async () => {
        const response = await nativeFetch(`${root}products/${encodeURIComponent(item.handle)}.js`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Purchase limits could not be checked. Please try again.');
        const product = await response.json();
        product.variants.forEach(variant => {
          variantProducts.set(String(variant.id), String(product.id ?? item.product_id ?? item.handle));
          comingSoon.set(String(variant.id), product.tags.includes('coming-soon'));
          limited.set(String(variant.id), product.tags.includes('one-per-customer'));
          bagChoices.set(String(variant.id), product.tags.includes('choose-one-bag'));
          exclusive.set(String(variant.id), product.tags.includes('one-per-customer') && product.tags.includes('disable-express-checkout'));
        });
      })().catch(error => { products.delete(item.handle); throw error; }));
      await products.get(item.handle);
      products.delete(item.handle);
    }));
  }
  function minimum(state) {
    const rate = state.currency === 'GBP' ? 1 : Number(window.Shopify?.currency?.rate);
    if ((state.currency !== 'GBP' && config.storeCurrency !== 'GBP') || !Number.isFinite(rate) || rate <= 0) {
      throw new Error('The order minimum could not be converted. Please select GBP to continue.');
    }
    const campaignQuantity = state.items.reduce((total, item) => total + (exclusive.get(String(item.variant_id)) ? Number(item.quantity) : 0), 0);
    const pounds = config.country === 'GB'
      ? (campaignQuantity >= 2 ? 30 : campaignQuantity > 0 ? 20 : 15)
      : (campaignQuantity >= 2 ? 40 : campaignQuantity > 0 ? 30 : 25);
    const threshold = Math.round(pounds * 100 * rate);
    const format = amount => new Intl.NumberFormat(document.documentElement.lang || 'en', { style:'currency', currency:state.currency }).format(amount / 100);
    return { blocked:state.total_price < threshold, message:state.total_price < threshold
      ? `Minimum order ${format(threshold)}. Add ${format(threshold - state.total_price)} to continue.`
      : `Minimum order ${format(threshold)} reached.` };
  }
  function bagChoiceConflict(items) {
    return new Set(items.filter(item => Number(item.quantity) > 0 && bagChoices.get(String(item.variant_id)))
      .map(item => variantProducts.get(String(item.variant_id)) ?? String(item.product_id ?? item.variant_id))).size > 1;
  }
  function renderBagChoice(state) {
    document.querySelectorAll('[data-bag-choice-message]').forEach(node => {
      node.hidden = !(bagChoiceRejected || bagChoiceConflict(state.items));
      node.textContent = node.hidden ? '' : bagChoiceMessage;
    });
  }
  async function showBagChoice() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer) {
      await customElements.whenDefined('cart-drawer-component');
      drawer.openFull();
    } else if (!location.pathname.replace(/\/$/, '').endsWith('/cart')) {
      sessionStorage.setItem('mlf-bag-choice-rejected', 'true');
      location.assign(`${root}cart`);
    }
  }
  bagChoiceRejected = sessionStorage.getItem('mlf-bag-choice-rejected') === 'true';
  sessionStorage.removeItem('mlf-bag-choice-rejected');
  function render(state) {
    renderBagChoice(state);
    let result;
    try { result = minimum(state); } catch (error) { result = { blocked:true, message:error.message }; }
    const exclusiveQuantity = state.items.reduce((total, item) => total + (exclusive.get(String(item.variant_id)) ? Number(item.quantity) : 0), 0);
    if (exclusiveQuantity > 2) {
      result = { blocked:true, message:campaignMessage };
    }
    try { validateQuantities(state.items); } catch (error) { result = { blocked:true, message:error.message }; }
    document.querySelectorAll('[data-purchase-minimum]').forEach(node => {
      node.hidden = !result.blocked;
      node.textContent = result.blocked ? result.message : '';
      node.toggleAttribute('data-blocked', result.blocked);
      node.toggleAttribute('data-campaign-limit', result.message === campaignMessage);
    });
    return result;
  }
  async function refresh() {
    const state = await cart();
    await hydrate(state.items);
    return render(state);
  }
  function validateQuantities(items, previous = []) {
    if (items.some(item => Number(item.quantity) > 0 && comingSoon.get(String(item.variant_id)))) throw new Error(comingSoonMessage);
    const productKey = item => variantProducts.get(String(item.variant_id)) ?? String(item.product_id ?? item.variant_id);
    const before = new Map();
    previous.forEach(item => {
      if (!limited.get(String(item.variant_id))) return;
      const key = productKey(item);
      before.set(key, (before.get(key) || 0) + Number(item.quantity));
    });
    const totals = new Map();
    items.forEach(item => {
      if (!limited.get(String(item.variant_id))) return;
      const key = productKey(item);
      totals.set(key, (totals.get(key) || 0) + Number(item.quantity));
    });
    for (const [id, quantity] of totals) {
      if (quantity > Math.max(1, before.get(id) || 0)) throw new Error('Only one of each product labelled "One per customer" may be purchased per customer per order, across all sizes and variants.');
    }
  }
  function payload(body) {
    if (body instanceof FormData || body instanceof URLSearchParams) return Object.fromEntries(body);
    return typeof body === 'string' ? JSON.parse(body) : body;
  }
  async function guard(url, options, path) {
    try {
      const state = await cart();
      await hydrate(state.items);
      const data = payload(options.body);
      let projected = state.items.map(item => ({ ...item }));
      if (path.includes('/add')) {
        scan();
        const additions = data.items || [{ id:data.id, quantity:data.quantity || 1 }];
        additions.forEach(item => {
          if (!comingSoon.has(String(item.id))) throw new Error('This item could not be checked. Please open its product page and try again.');
          if (comingSoon.get(String(item.id))) throw new Error(comingSoonMessage);
          projected.push({ variant_id:item.id, quantity:item.quantity || 1 });
        });
      } else if (path.includes('/change')) {
        const index = data.line ? Number(data.line) - 1 : projected.findIndex(item => item.key === String(data.id) || String(item.variant_id) === String(data.id));
        if (projected[index]) projected[index].quantity = Number(data.quantity);
      } else if (data.updates) {
        if (Array.isArray(data.updates)) projected.forEach((item,index) => { if (data.updates[index] != null) item.quantity = Number(data.updates[index]); });
        else projected.forEach(item => { const value = data.updates[item.key] ?? data.updates[item.variant_id]; if (value != null) item.quantity = Number(value); });
      }
      if (bagChoiceConflict(projected)) {
        bagChoiceRejected = true;
        renderBagChoice(state);
        return new Response(JSON.stringify({ status:422, cartOnlyBagChoice:true }), {
          status:422, headers:{ 'Content-Type':'application/json' }
        });
      }
      validateQuantities(projected, state.items);
      const response = await nativeFetch(url, options);
      if (response.ok) {
        bagChoiceRejected = false;
        // Cart response sections are handled by the existing theme components.
        refresh().catch(() => {});
      }
      return response;
    } catch (error) {
      notice(error.message);
      return new Response(JSON.stringify({ status:422, message:error.message, description:error.message, errors:error.message }), { status:422, headers:{ 'Content-Type':'application/json' } });
    }
  }
  window.fetch = function(url, options = {}) {
    const path = new URL(typeof url === 'string' ? url : url.url, location.href);
    if (path.origin !== location.origin || !/\/cart\/(add|change|update)(\.js)?$/.test(path.pathname) || !options.body) return nativeFetch(url, options);
    const run = queue.then(() => guard(url, options, path.pathname));
    queue = run.catch(() => {});
    return run;
  };
  // Check fresh cart state immediately before leaving the storefront.
  let approved = false;
  async function checkout(event, resume) {
    if (approved) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await queue;
      const state = await cart();
      await hydrate(state.items);
      const result = render(state);
      if (bagChoiceConflict(state.items)) {
        await showBagChoice();
        return;
      }
      if (result.blocked) {
        notice(result.message);
        return;
      }
      validateQuantities(state.items);
      approved = true;
      resume();
      setTimeout(() => { approved = false; }, 0);
    } catch (error) { notice(error.message); }
  }
  document.addEventListener('click', event => {
    const target = event.target.closest?.('[name="checkout"], a[href*="/checkout"]');
    if (target) checkout(event, () => target.click());
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.submitter?.name === 'checkout' || /\/checkout/.test(form.action)) {
      checkout(event, () => form.requestSubmit(event.submitter || undefined));
    } else if (/\/cart\/add/.test(form.action) && !form.closest('product-form-component') && !form.matches('[data-preview-form]')) {
      event.preventDefault();
      notice('Please use Add to cart to check purchase limits.');
    }
  }, true);
  document.addEventListener('mlf:bag-choice-rejected', () => { showBagChoice().catch(() => {}); });
  let refreshTimer;
  new MutationObserver(() => {
    scan();
    if (!document.querySelector('[data-purchase-minimum]:not([data-ready])')) return;
    document.querySelectorAll('[data-purchase-minimum]').forEach(node => { node.dataset.ready = ''; });
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refresh().catch(() => {}), 100);
  }).observe(document.body, { childList:true, subtree:true });
  scan();
  refresh().catch(() => {});
})();
